import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { MdViewStream, MdViewColumn } from 'react-icons/md'
import useTranslation from '../../hooks/useTranslation'
import ConnectField from '../select/ConnectField'
import { Icon_Arrow_Left_Simple, Icon_Arrow_Left, Icon_Arrow_Right } from '../../styles/icons'

const addHours = (date, h) => new Date(date.getTime() + h * 3600000)

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1.25rem;
    border-bottom: 1px solid ${({ theme }) => theme.area.lightBorder};
    background: ${({ theme }) => theme.card.background};
    flex-shrink: 0;
    flex-wrap: wrap;
`

const BackButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    display: flex;
    align-items: center;
    padding: 0.2rem;
    border-radius: 0.25rem;
    font-size: 1rem;
    flex-shrink: 0;
    transition: color 0.15s, background 0.15s;

    &:hover {
        color: ${({ theme }) => theme.font.color};
        background: ${({ theme }) => theme.form.panelBg};
    }
`

const ConnectWrapper = styled.div`
    width: ${({ $width }) => $width ?? '300px'};
    flex-shrink: 1;
    min-width: 180px;
`

const LayoutSwitcher = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;

    @media (max-width: 768px) {
        display: none;
    }
`

const LayoutBtn = styled.button`
    background: ${({ $active, theme }) => $active ? theme.btn.primary : 'none'};
    border: 1px solid ${({ $active, theme }) => $active ? theme.btn.primary : theme.area.lightBorder};
    color: ${({ $active, theme }) => $active ? theme.card.background : theme.font.label};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.3rem;
    font-size: 1rem;
    transition: color 0.15s, background 0.15s, border-color 0.15s;

    &:hover {
        color: ${({ $active, theme }) => $active ? theme.card.background : theme.font.color};
        background: ${({ $active, theme }) => $active ? theme.btn.primary : theme.area.highBg};
        border-color: ${({ $active, theme }) => $active ? theme.btn.primary : theme.form.border};
    }
`

const ShiftNav = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
`

const NavButton = styled.button`
    background: none;
    border: 1px solid ${({ theme }) => theme.area.lightBorder};
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.3rem;
    font-size: 0.9rem;
    transition: color 0.15s, background 0.15s, border-color 0.15s;

    &:hover {
        color: ${({ theme }) => theme.font.color};
        background: ${({ theme }) => theme.area.highBg};
        border-color: ${({ theme }) => theme.form.border};
    }
`

const ShiftLabel = styled.span`
    font-size: var(--font-S);
    font-weight: 500;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    min-width: 220px;
    text-align: center;
`

const formatShiftLabel = (start, hours) => {
    const end = addHours(start, hours)
    const datePart = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    const fmt = (d) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    return `${datePart}  ·  ${fmt(start)} – ${fmt(end)}`
}

const WorkPlannerHeader = ({ plantSelectorConfig, plantValue, onPlantChange, selectorConfig, workOrderValue, onWorkOrderChange, shiftStart, shiftHours, onPrev, onNext, boardLayout, onLayoutChange }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    return (
        <Header>
            <BackButton onClick={() => navigate(-1)}>{Icon_Arrow_Left_Simple}</BackButton>

            {plantSelectorConfig && (
                <ConnectWrapper $width={plantSelectorConfig.width}>
                    <ConnectField config={plantSelectorConfig} value={plantValue} onChange={onPlantChange} />
                </ConnectWrapper>
            )}

            <ConnectWrapper $width={selectorConfig.width}>
                <ConnectField config={selectorConfig} value={workOrderValue} onChange={onWorkOrderChange} />
            </ConnectWrapper>

            {onLayoutChange && (
                <LayoutSwitcher>
                    <LayoutBtn
                        $active={boardLayout === 'horizontal'}
                        onClick={() => onLayoutChange('horizontal')}
                        title={t('manufacturing.planner.header.layoutHorizontal')}
                    >
                        <MdViewStream />
                    </LayoutBtn>
                    <LayoutBtn
                        $active={boardLayout === 'vertical'}
                        onClick={() => onLayoutChange('vertical')}
                        title={t('manufacturing.planner.header.layoutVertical')}
                    >
                        <MdViewColumn />
                    </LayoutBtn>
                </LayoutSwitcher>
            )}

            <ShiftNav>
                <NavButton onClick={onPrev} title={t('manufacturing.planner.header.prev')}>
                    {Icon_Arrow_Left}
                </NavButton>
                <ShiftLabel>{formatShiftLabel(shiftStart, shiftHours)}</ShiftLabel>
                <NavButton onClick={onNext} title={t('manufacturing.planner.header.next')}>
                    {Icon_Arrow_Right}
                </NavButton>
            </ShiftNav>
        </Header>
    )
}

export default WorkPlannerHeader
